/**
 * 28. Cash Management — cash position forecast, bank balances, cash
 * movements.
 *
 * The position heads in the workspace currency; accounts in any other
 * currency are listed beneath it, not converted (CUR-2).
 *
 * Arabic and English (C7): every word from the catalogs; the change is a
 * percentage the locale writes.
 */
import {
  Card,
  PageHeader,
  Skeleton,
  StatCard,
  DataTable,
} from "@/components/ui";
import { OtherCurrencies } from "@/components/Money";
import { QueryError } from "@/components/QueryError";
import { useApiQuery } from "@/lib/api/hooks";
import type { CurrencyAmount } from "@/lib/currency";
import { useTranslation } from "@/lib/i18n";
import { formatSignedPercent } from "@/lib/i18n/format";
import { formatMoney, formatDate } from "@/lib/money";

interface CashManagementData {
  forecast: {
    balance: string;
    currency: string;
    changePct: number;
    others: CurrencyAmount[];
  };
  bankBalances: Array<{
    id: string;
    bankAccountName: string;
    balance: string;
    currencyCode: string;
  }>;
  movements: Array<{
    id: string;
    date: string;
    description: string;
    inflow: string;
    outflow: string;
    currencyCode: string;
  }>;
}

export function CashManagementPage() {
  const { t } = useTranslation();
  const { data, isPending, error, refetch, isFetching } =
    useApiQuery<CashManagementData>(
      ["finance", "cash-management"],
      "/finance/cash-management",
    );

  if (isPending) return <Skeleton rows={10} />;
  if (error || !data)
    return (
      <QueryError
        error={error}
        retrying={isFetching}
        onRetry={() => void refetch()}
      />
    );

  return (
    <div>
      <PageHeader
        title={t("finance.cash.title")}
        subtitle={t("finance.cash.subtitle")}
      />

      <div className="atlas-grid atlas-grid--3 mb-4">
        <StatCard
          label={t("finance.cash.forecast")}
          value={formatMoney(data.forecast.balance, data.forecast.currency)}
          detail={<OtherCurrencies others={data.forecast.others} />}
          meta={formatSignedPercent(data.forecast.changePct)}
          tone={data.forecast.changePct >= 0 ? "positive" : "negative"}
        />
      </div>

      <div className="atlas-grid atlas-grid--2">
        <Card title={t("finance.cash.bankBalances")}>
          <DataTable
            rows={data.bankBalances}
            columns={[
              { key: "bankAccountName", label: t("finance.cash.bankAccount") },
              {
                key: "balance",
                label: t("fields.balance"),
                numeric: true,
                render: (r) => formatMoney(r.balance, r.currencyCode),
              },
            ]}
          />
        </Card>
        <Card title={t("finance.cash.movements")}>
          <DataTable
            rows={data.movements}
            columns={[
              {
                key: "date",
                label: t("fields.date"),
                render: (r) => formatDate(r.date),
              },
              { key: "description", label: t("fields.description") },
              {
                key: "inflow",
                label: t("finance.cash.in"),
                numeric: true,
                render: (r) =>
                  Number(r.inflow) > 0
                    ? formatMoney(r.inflow, r.currencyCode)
                    : "—",
              },
              {
                key: "outflow",
                label: t("finance.cash.out"),
                numeric: true,
                render: (r) =>
                  Number(r.outflow) > 0
                    ? formatMoney(r.outflow, r.currencyCode)
                    : "—",
              },
            ]}
          />
        </Card>
      </div>
    </div>
  );
}
